import React, { useEffect, useState } from 'react';
import PopupScreen from './PopupScreen';
import { Typography, Box } from "@mui/material";
import { getRandomMedImage } from './imageUtils';

interface GameOverScreenProps {
  open: boolean;
  reason: string;
  onRestart: () => void;
  onViewBoard: () => void;
  onCredits: () => void;
  moveCount: number;
  isMobile: boolean; // Added prop for mobile detection
  isLandscape?: boolean; // Added prop for landscape detection
}

const GameOverScreen: React.FC<GameOverScreenProps> = ({ 
  open, 
  reason, 
  onRestart, 
  onViewBoard, 
  onCredits, 
  moveCount, 
  isMobile,
  isLandscape = false // Default to false if not provided
}) => {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [randomImage, setRandomImage] = useState<string>("");

  // Preload the random image before showing the component
  useEffect(() => {
    if (open) {
      // Reset ready state when opening
      setIsReady(false);
      
      // Get random image
      const image = getRandomMedImage();
      setRandomImage(image);
      
      // Preload the image
      const imageObj = new Image();
      imageObj.onload = () => {
        setIsReady(true); // Mark component as ready once image is loaded
      };
      imageObj.src = image;
    }
  }, [open]);

  // Define buttons for the game over screen
  const gameOverButtons = [
    {
      text: "Restart Game",
      onClick: onRestart,
      color: 'primary' as const,
      variant: 'contained' as const,
      delay: 8000,
      playAudio: true
    },
    {
      text: "View Board",
      onClick: onViewBoard,
      color: 'primary' as const,
      variant: 'contained' as const
    },
    {
      text: "About",
      onClick: onCredits,
      color: 'secondary' as const, 
      variant: 'contained' as const
    }
  ];

  // Create a combined reason that includes both the game over reason and move count
  const gameOverReason = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography 
        sx={{ 
          color: 'white', 
          fontFamily: "'Playfair Display', serif",
          fontStyle: 'italic',
          fontSize: isMobile ? '0.9rem' : '1.15rem'
        }}
      >
        {reason}
      </Typography>
      <Typography 
        sx={{ 
          color: 'white',
          fontWeight: 'bold',
          fontSize: isMobile ? '0.9rem' : '1.1rem',
          mt: 0
        }}
      >
        # Moves: {moveCount}
      </Typography>
    </Box>
  );

  return (
    <PopupScreen
      open={open && isReady} // Only show when ready
      title="Game Over"
      imageSrc={randomImage}
      audioSrc="/sounds/ocean1.wav"
      customReason={gameOverReason}
      buttons={gameOverButtons}
      isMobile={isMobile}
      isLandscape={isLandscape}
    />
  );
};

export default GameOverScreen;
